/**
 * Encryption Utility
 * 
 * Provides functions for encrypting and decrypting sensitive data
 */

/**
 * Generates a cryptographic key from a passphrase and salt
 * @param {string} salt - Random salt to use in key derivation
 * @param {string} passphrase - Secret passphrase to derive the key from
 * @returns {Promise<CryptoKey>} - The generated key
 */
export const generateKey = async (salt, passphrase) => {
  try {
    // Ensure consistent input encoding
    const encoder = new TextEncoder();
    const passphraseBuffer = encoder.encode(passphrase);
    const saltBuffer = encoder.encode(salt);
    
    // First, create a base key from the passphrase
    const baseKey = await window.crypto.subtle.importKey(
      'raw',
      passphraseBuffer,
      { name: 'PBKDF2' },
      false,
      ['deriveKey']
    );
    
    // Use consistent parameters for PBKDF2
    const iterations = 100000; // High iteration count for security
    
    // Derive the actual encryption key - using AES-CBC instead of AES-GCM
    const derivedKey = await window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: saltBuffer,
        iterations: iterations,
        hash: 'SHA-256'
      },
      baseKey,
      {
        name: 'AES-CBC',
        length: 256
      },
      false, // non-extractable
      ['encrypt', 'decrypt']
    );
    
    return derivedKey;
  } catch (error) {
    console.error('Error generating encryption key:', error);
    throw new Error('Failed to generate encryption key');
  }
};

/**
 * Safe version of btoa that can handle large strings
 * @param {string} str - String to encode
 * @returns {string} - Base64 encoded string
 */
const safebtoa = (str) => {
  // btoa can fail with large strings, so we need to chunk it
  const CHUNK_SIZE = 8192;
  let result = '';
  
  for (let i = 0; i < str.length; i += CHUNK_SIZE) {
    const chunk = str.slice(i, i + CHUNK_SIZE);
    result += btoa(chunk);
  }
  
  return result;
};

/**
 * Encrypts data using AES-CBC
 * @param {string} data - Data to encrypt
 * @param {CryptoKey} key - Key to use for encryption
 * @returns {Promise<string>} - Base64 encoded encrypted data
 */
export const encrypt = async (data, key) => {
  try {
    
    // Generate a random IV (16 bytes for AES-CBC)
    const iv = window.crypto.getRandomValues(new Uint8Array(16));
    
    // Convert data to ArrayBuffer
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    
    // Encrypt the data
    const encryptedBuffer = await window.crypto.subtle.encrypt(
      {
        name: 'AES-CBC',
        iv
      },
      key,
      dataBuffer
    );
    
    // Create a combined buffer with IV + encrypted data
    const resultBuffer = new Uint8Array(iv.length + encryptedBuffer.byteLength);
    resultBuffer.set(iv, 0);
    resultBuffer.set(new Uint8Array(encryptedBuffer), iv.length);
    
    // Convert to Base64 for storage - use a chunked approach to avoid call stack issues
    let binary = '';
    const bytes = resultBuffer;
    const chunkSize = 1024;
    
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.slice(i, i + chunkSize);
      binary += String.fromCharCode.apply(null, chunk);
    }
    
    const base64Result = btoa(binary);
    
    return base64Result;
  } catch (error) {
    console.error('Error encrypting data:', error);
    throw new Error('Failed to encrypt data');
  }
};

/**
 * Decrypts data using AES-CBC
 * @param {string} encryptedData - Base64 encoded encrypted data
 * @param {CryptoKey} key - Key to use for decryption
 * @returns {Promise<string>} - Decrypted data
 */
export const decrypt = async (encryptedData, key) => {
  try {

    // Convert Base64 to binary
    const binaryString = atob(encryptedData);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    // Extract IV (first 16 bytes for AES-CBC)
    const iv = bytes.slice(0, 16);
    
    // Extract encrypted data (everything after IV)
    const encryptedBuffer = bytes.slice(16);
    
    // Decrypt the data
    const decryptedBuffer = await window.crypto.subtle.decrypt(
      {
        name: 'AES-CBC',
        iv
      },
      key,
      encryptedBuffer
    );
    
    // Convert ArrayBuffer to string
    const decoder = new TextDecoder();
    const decryptedText = decoder.decode(decryptedBuffer);
    
    return decryptedText;
  } catch (error) {
    console.error('Error decrypting data:', error);
    throw new Error('Failed to decrypt data');
  }
};

/**
 * Generates a random string of specified length
 * @param {number} length - Length in bytes (will result in twice as many hex chars)
 * @returns {string} Random hex string
 */
export const generateRandomString = (length = 32) => {
  const buffer = new Uint8Array(length);
  window.crypto.getRandomValues(buffer);
  return Array.from(buffer)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

/**
 * Obfuscate a key by adding random prefix and suffix
 * @param {string} key - Key to obfuscate
 * @returns {string} Obfuscated key
 */
export const obfuscateKey = (key) => {
  if (!key) return '';
  const prefix = generateRandomString(8);
  const suffix = generateRandomString(8);
  return `${prefix}${key}${suffix}`;
};

/**
 * Deobfuscate a key by removing random prefix and suffix
 * @param {string} obfuscatedKey - Obfuscated key
 * @returns {string} Original key
 */
export const deobfuscateKey = (obfuscatedKey) => {
  if (!obfuscatedKey || obfuscatedKey.length < 32) return '';
  return obfuscatedKey.slice(16, -16);
};

export default {
  generateKey,
  encrypt,
  decrypt,
  generateRandomString,
  obfuscateKey,
  deobfuscateKey
};
